/**
 * What a stored file URL actually IS — extension-based, query-aware.
 *
 * A transaction image/document column stores the SERVED url of the upload, and
 * the served url rarely ends in the file's name: it is
 * `…/documents/raw?file=<opaque>.png&name=<the real name>`. So the classifier
 * reads the display name (the `name` parameter when there is one) rather than
 * the path, and everything that has to answer "is this an image?" reads it from
 * here — the upload widget's inline preview, and the Assistant's result table,
 * which shows a thumbnail rather than a wall of url text.
 *
 * A leaf module on purpose: the Assistant's chat needs the two functions, not
 * the upload widget they were extracted from.
 */

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif', 'ico']
const TEXT_EXT = ['txt', 'csv', 'json', 'xml', 'md', 'log', 'yaml', 'yml', 'tsv']

/** The file's own name: the `name` query parameter, else the last path segment. */
export function displayNameForUrl(url: string): string {
  if (!url) return ''
  try {
    const u = new URL(url, window.location.origin)
    const n = u.searchParams.get('name')
    if (n) return n
    const last = u.pathname.split('/').filter(Boolean).pop()
    return last ? decodeURIComponent(last) : url
  } catch {
    const q = url.split('?')[0]
    return q.split('/').filter(Boolean).pop() || url
  }
}

/** Classify a stored file by the extension on its (query-aware) display name. */
export function detectFileKind(url: string): 'image' | 'pdf' | 'text' | 'other' {
  const name = displayNameForUrl(url)
  const ext = (name.split('.').pop() || '').toLowerCase()
  if (IMAGE_EXT.includes(ext)) return 'image'
  if (ext === 'pdf') return 'pdf'
  if (TEXT_EXT.includes(ext)) return 'text'
  return 'other'
}
